import React, { useEffect, useMemo, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { API_BASE, AUTH_TOKEN, edgeInset } from '../config';
import { proxied } from '../services/assets';
import { audioEngine } from '../services/audioEngine';
import { myRecordFor, refreshMyRecords, releasedCount } from '../services/myRecords';

// RecordPlaquePanel — the wall of gold records is where you put a record OUT.
//
// This is the diegetic home for two functions that had none. Artwork and
// release were website buttons; here they are things you do to a plaque on the
// studio wall, in the room where the track was made. Design law 1 asks for
// exactly this — hide the function, deliver the result — and a wall of framed
// records is a far better answer than a producer asking "want me to put it out?"
// because it is a PLACE. It is still there tomorrow, it fills up as you work,
// and the empty frames are the part that does the arguing.
//
// RELEASE IS ALWAYS THE ARTIST'S CALL. Publishing posts to the public feed and
// puts the track into station rotation, so nothing here happens on the player's
// behalf — this panel is the yes, and it says plainly what the yes does.
export default function RecordPlaquePanel() {
  const activePlaque = useWorldStore((s) => s.activePlaque);
  const setActivePlaque = useWorldStore((s) => s.setActivePlaque);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);

  const [busy, setBusy] = useState('');      // 'artwork' | 'release'
  const [note, setNote] = useState('');
  const [tick, setTick] = useState(0);

  // activePlaque IS the slot: frame 1 holds your newest record, frame 2 the one
  // before it. The wall is authored in rooms.js, so there is no placement row to
  // look anything up by.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const track = useMemo(() => (activePlaque == null ? null : myRecordFor(null, activePlaque)), [activePlaque, tick]);

  useEffect(() => { setNote(''); setBusy(''); }, [activePlaque]);

  if (activePlaque == null) return null;   // 0 is a real frame

  const close = () => setActivePlaque(null);
  const released = track && (track.status === 'released' || track.is_released);

  const call = async (path, body) => {
    const res = await fetch(`${API_BASE}/api/tracks/${track.track_id || track.id}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `${path} failed`);
    return data;
  };

  const makeArtwork = async () => {
    setBusy('artwork'); setNote('');
    try {
      await call('generate-artwork');
      await refreshMyRecords();
      setTick((n) => n + 1);
      setNote('New artwork is on the wall.');
    } catch (err) { setNote(err.message); } finally { setBusy(''); }
  };

  const release = async () => {
    setBusy('release'); setNote('');
    try {
      await call('publish');
      await refreshMyRecords();
      setTick((n) => n + 1);
      // Quests are PULL-based, so nothing knows a record went out until we ask.
      // The wall quest counts releases; this is the ask.
      const synced = await syncWall();
      setNote(synced?.justCompleted
        ? "That's the wall full. All four."
        : "It's out. It'll come round on the station.");
    } catch (err) { setNote(err.message); } finally { setBusy(''); }
  };

  const syncWall = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/quests/wall_of_four/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` },
        body: JSON.stringify({ avatarId: myAvatarId }),
      });
      return await res.json().catch(() => ({}));
    } catch { return {}; }
  };

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ font: '800 12px system-ui', letterSpacing: 1.2, color: '#ffb25e' }}>
          ON THE WALL <span style={{ opacity: 0.55, marginLeft: 4 }}>{Math.min(releasedCount(), 4)}/4</span>
        </span>
        <button onClick={close} style={iconBtn} aria-label="Close">×</button>
      </div>

      {!track ? (
        // An empty frame is the point, not a failure state.
        <div style={{ textAlign: 'center', padding: '18px 6px 10px' }}>
          <div style={{ ...art, display: 'grid', placeItems: 'center', border: '1px dashed rgba(255,178,94,0.4)', background: 'rgba(255,255,255,0.03)' }}>
            <span style={{ font: '700 12px system-ui', opacity: 0.45 }}>empty frame</span>
          </div>
          <div style={{ font: '700 14px system-ui', marginTop: 12 }}>Nothing here yet</div>
          <div style={{ font: '600 12px system-ui', opacity: 0.55, marginTop: 5, lineHeight: 1.5 }}>
            Make a record in this room and it goes up here.
          </div>
        </div>
      ) : (
        <>
          <img
            src={track.artwork_url ? proxied(track.artwork_url) : ''}
            alt=""
            style={{ ...art, objectFit: 'cover', background: '#1a1520' }}
          />
          <div style={{ font: '800 15px system-ui', marginTop: 11 }}>{track.title || 'Untitled'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5 }}>
            <span style={{
              font: '700 9.5px system-ui', letterSpacing: 0.6, padding: '2px 7px', borderRadius: 999,
              background: released ? 'rgba(157,255,125,0.16)' : 'rgba(255,210,125,0.16)',
              color: released ? '#9dff7d' : '#ffd27d',
            }}>{released ? 'RELEASED' : 'UNRELEASED'}</span>
            <button onClick={() => audioEngine.playTrack(track)} style={linkBtn}>▶ Play</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 13 }}>
            <button onClick={makeArtwork} disabled={!!busy} style={btn(false)}>
              {busy === 'artwork' ? 'Making artwork…' : track.artwork_url ? 'New artwork' : 'Make artwork'}
            </button>
            {!released && (
              <button onClick={release} disabled={!!busy} style={btn(true)}>
                {busy === 'release' ? 'Putting it out…' : 'Put it out'}
              </button>
            )}
          </div>

          {!released && (
            // Say what the yes does. Releasing is not undoable in the way a
            // player would assume from a button labelled "Put it out".
            <div style={{ font: '600 10.5px system-ui', opacity: 0.45, marginTop: 9, lineHeight: 1.5 }}>
              Posts to the public feed and puts it in rotation on the station.
            </div>
          )}
          {note && <div style={{ font: '600 11.5px system-ui', color: '#ffd27d', marginTop: 9 }}>{note}</div>}
        </>
      )}
    </div>
  );
}

const wrap = {
  position: 'fixed', right: edgeInset(16, 'right'), top: '96px', zIndex: 73,
  width: 'min(268px, calc(100vw - 32px))', maxHeight: '72vh', overflowY: 'auto',
  background: 'rgba(14,9,6,0.95)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 14,
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', color: '#ffe9c4',
  fontFamily: 'system-ui, sans-serif', padding: 13, pointerEvents: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
const art = { width: '100%', aspectRatio: '1 / 1', borderRadius: 10, display: 'block' };
const iconBtn = { cursor: 'pointer', background: 'none', border: 'none', color: '#ffe9c4', fontSize: 19, lineHeight: 1, padding: 2 };
const linkBtn = { cursor: 'pointer', background: 'none', border: 'none', color: '#ffb25e', font: '700 11.5px system-ui', padding: 0 };
const btn = (primary) => ({
  padding: '9px 12px', borderRadius: 10, cursor: 'pointer', width: '100%',
  border: primary ? 'none' : '1px solid rgba(255,178,94,0.45)',
  background: primary ? 'linear-gradient(180deg,#ffb25e,#ff5e78)' : 'rgba(255,178,94,0.12)',
  color: primary ? '#1a0f00' : '#ffe9c4', font: '700 12.5px system-ui',
});
